import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { SystemDropdownCategory } from "@/api";
import { getSystemDropdownCategoryLabel } from "@/pages/system-settings/tabs/system-dropdowns/SystemDropdownsSettings.utils";
import { useTranslation } from "@/translations";

const CATEGORY_PARAM = "category";
// SystemDropdownCategory has members, so Object.values is non-empty.
const CATEGORIES = Object.values(SystemDropdownCategory) as [SystemDropdownCategory, ...SystemDropdownCategory[]];

function parseCategoryParam(value: string | null): SystemDropdownCategory | null {
  if (value && CATEGORIES.includes(value as SystemDropdownCategory)) {
    return value as SystemDropdownCategory;
  }
  return null;
}

export function useSortedSystemDropdownCategories() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortedCategories = useMemo(() => {
    const labeled = CATEGORIES.map((category) => ({ category, label: getSystemDropdownCategoryLabel(category, t) }));
    labeled.sort((a, b) => a.label.localeCompare(b.label, i18n.language));
    return labeled.map((item) => item.category);
  }, [t, i18n.language]);

  const selectedCategory: SystemDropdownCategory =
    parseCategoryParam(searchParams.get(CATEGORY_PARAM)) ?? sortedCategories[0] ?? CATEGORIES[0];

  const setSelectedCategory = (category: SystemDropdownCategory) => {
    setSearchParams({ [CATEGORY_PARAM]: category });
  };

  return { sortedCategories, selectedCategory, setSelectedCategory };
}
